// public/v3/src/lib/ccm-tier-progress.js — pure helpers for the Time Logs
// CCM milestone bar + runway lenses.
//
// CJS (Jest-loadable) like ccm-tier-bump.js; time-logs.jsx imports via ESM
// interop. Pure functions only — no React, no fetches.
//
// The CCM minute ladder for a 99490-family month (repo rates, billing-
// generate.js): 20 min bills 99490 ($76.02), each further 20 min stacks a
// 99439 add-on ($58.16, ×2 cap), and 90 min opens the 99487/99491 tier bump
// (Studio affirmation) where Complex out-pays the capped 99490 stack. The
// bar makes "base met, runway remaining" visible so add-on/complex minutes
// are never left on the table; the enrollment-time UI (Onboard) is tier-
// agnostic by design — the subprogram is decided HERE, monthly.

const { TIER_BUMP_MIN_MINUTES } = require('./ccm-tier-bump');

// billing.program values that ride the CCM minute ladder. (PCM/APCM/BHI have
// flat single targets and keep the simple bar.)
const CCM_TIER_CODES = new Set(['99490', '99491', '99487']);

// Milestone ticks, in minutes. 90 = TIER_BUMP_MIN_MINUTES.
const CCM_MILESTONES = [
  { min: 20, label: '99490', title: '99490 base — first 20 staff minutes' },
  { min: 40, label: '+99439', title: '99439 add-on — each additional 20 minutes' },
  { min: 60, label: '+99439×2', title: 'Second 99439 add-on (cap) — 60 staff minutes' },
  { min: TIER_BUMP_MIN_MINUTES, label: 'complex', title: '99487/99491 tier bump opens at 90 minutes (affirmed at generate time)' },
];

function isCcmTierPatient(raw) {
  return CCM_TIER_CODES.has(String(((raw || {}).billing || {}).program || '').trim());
}

// ccmMilestoneProgress(minutes) → { mins, pct, color, next, met }
//   pct   — fill % against the 90-min bump gate (display scale)
//   color — 'red' below the 20-min base (nothing billable yet),
//           'yellow' base met with add-on/complex runway remaining,
//           'green' at ≥90 (the ladder is fully climbed)
//   next  — the first unmet milestone (null at ≥90)
function ccmMilestoneProgress(minutes) {
  const mins = Math.max(0, Math.floor(Number(minutes) || 0));
  const met = CCM_MILESTONES.filter((m) => mins >= m.min);
  const next = CCM_MILESTONES.find((m) => mins < m.min) || null;
  const color = mins < 20 ? 'red' : (mins >= TIER_BUMP_MIN_MINUTES ? 'green' : 'yellow');
  return { mins, pct: Math.min(100, (mins / TIER_BUMP_MIN_MINUTES) * 100), color, next, met };
}

// Simple-target color for flat-target programs (BHI 20, Pain 30, PCM 30):
// green = target met, red = nothing logged, yellow = started but short.
function simpleTargetColor(minutes, target) {
  const mins = Math.max(0, Number(minutes) || 0);
  if (target > 0 && mins >= target) return 'green';
  return mins > 0 ? 'yellow' : 'red';
}

// Runway lens for the Status filter. Returns:
//   'addon'   — 99490 base met, next 99439 unit still reachable (20–59 min)
//   'complex' — add-ons capped and climbing toward 90 (60–89 min), OR ≥90
//               with no ccmTierMonth affirmation yet (bump click owed)
//   null      — below base, fully resolved (≥90 + affirmed), or not a CCM-
//               ladder patient
function ccmRunwayLens(raw, monthStr, minutes) {
  if (!isCcmTierPatient(raw)) return null;
  const mins = Math.max(0, Number(minutes) || 0);
  if (mins < 20) return null;
  if (mins < 60) return 'addon';
  const bump = ((raw || {}).ccmTierMonth || {})[monthStr];
  const affirmed = !!(bump && bump.code);
  if (mins < TIER_BUMP_MIN_MINUTES || !affirmed) return 'complex';
  return null;
}

module.exports = {
  CCM_MILESTONES,
  isCcmTierPatient,
  ccmMilestoneProgress,
  simpleTargetColor,
  ccmRunwayLens,
};
